import { Injectable, signal } from '@angular/core';

export interface RecorderTheme {
  id: string;
  name: string;
  builtIn: boolean;
  /** Tokens to set on :root. Empty = use SCSS defaults. */
  tokens: Record<string, string>;
  /**
   * When defined, the fill-light brightness slider brightens/darkens the
   * theme's own background color instead of the default white-light overlay.
   * `dark` = color at 0% brightness, `light` = color at 100% brightness.
   *
   * Optional `tone.cool` / `tone.warm` override the universal blue→white→amber
   * tone slider with theme-specific endpoints. When omitted, the recorder
   * falls back to the universal cool/warm range.
   */
  fillLight?: {
    dark: [number, number, number];
    light: [number, number, number];
    tone?: {
      cool: [number, number, number];
      warm: [number, number, number];
    };
  };
  /**
   * Optional animation function. Called every animation frame with elapsed
   * milliseconds; returns CSS token overrides applied on top of `tokens`.
   * Paused automatically during recording.
   */
  animation?: (ms: number) => Record<string, string>;
}

// ── Animation helpers ─────────────────────────────────────────────────────────

function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)))).toString(16).padStart(2, '0');
  };
  return '#' + f(0) + f(8) + f(4);
}

function hslRgb(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  return `${f(0)},${f(8)},${f(4)}`;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return a + diff * t;
}

// ── Live theme animation functions ────────────────────────────────────────────

const auroraLiveAnim = (ms: number): Record<string, string> => {
  const t  = (ms % 24000) / 24000 * Math.PI * 2;
  const t2 = (ms % 9000)  / 9000  * Math.PI * 2;
  const bgH  = 225 + 60 * Math.sin(t);
  const lDark = 8  + 4  * Math.sin(t2);
  const lMid  = 16 + 6  * Math.sin(t2 + 1);
  const dark = hslToHex(bgH,      90, lDark);
  const mid  = hslToHex(bgH + 18, 84, lMid);
  const panelRgb = hslRgb(bgH, 88, lDark + 4);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(bgH, 88, lDark)},0.92)`,
  };
};

const sunriseAnim = (ms: number): Record<string, string> => {
  // A literal sunrise: a fixed vertical sky — deep indigo up top, down through
  // violet and magenta-pink, to a warm gold glow at the horizon. Over the cycle
  // the whole sky brightens and the warm horizon band climbs upward as the sun
  // rises; a sine drive makes the loop seamless (rise → settle → rise).
  const period = 48000;
  const rise = (Math.sin((ms % period) / period * Math.PI * 2 - Math.PI / 2) + 1) / 2;
  const l = (base: number) => base + rise * 8;        // the whole sky lifts with the sun
  const sky  = hslToHex(250, 55, l(6));               // upper sky — deep indigo
  const high = hslToHex(278, 58, l(9));               // violet
  const mid  = hslToHex(325, 66, l(13));              // magenta-pink
  const low  = hslToHex(8,   82, l(16));              // coral
  const glow = hslToHex(34,  92, l(18) + rise * 6);   // warm gold horizon — brightest
  const horizon = 100 - rise * 22;                    // warm band climbs as the sun rises
  const surface = hslToHex(258, 46, l(7));            // dark indigo panels/modals
  const panelRgb = hslRgb(250, 42, l(4));
  return {
    '--shell-bg':
      `linear-gradient(180deg, ${sky} 0%, ${high} 34%, ${mid} 58%, ${low} ${(horizon - 14).toFixed(1)}%, ${glow} ${horizon.toFixed(1)}%)`,
    '--shell-surface':  sky,
    '--no-cam-bg':      high,
    '--modal-card-bg':  surface,
    '--modal-inset-bg': sky,
    '--hint-bar-bg':    surface,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(250, 46, l(3))},0.92)`,
  };
};

const breatheAnim = (ms: number): Record<string, string> => {
  const breath = (Math.sin((ms % 6000) / 6000 * Math.PI * 2 - Math.PI / 2) + 1) / 2;
  const hDrift = 220 + 25 * Math.sin((ms % 20000) / 20000 * Math.PI * 2);
  const l1 = 3  + breath * 15;
  const l2 = 6  + breath * 18;
  const dark     = hslToHex(hDrift,     78, l1);
  const mid      = hslToHex(hDrift - 8, 72, l2);
  const panelRgb = hslRgb(hDrift, 76, l1 + 5);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(hDrift, 78, l1)},0.92)`,
  };
};

/** Pulse — rainbow hue cycle (6 s) with a 4 s lightness pulse. */
const pulseAnim = (ms: number): Record<string, string> => {
  const h = (ms / 6000 * 360) % 360;
  const lPulse = (Math.sin((ms / 4000) * Math.PI * 2) + 1) / 2;
  const l1 = 6 + lPulse * 6;
  const l2 = 12 + lPulse * 8;
  const dark     = hslToHex(h,                  82, l1);
  const mid      = hslToHex((h + 18) % 360,     76, l2);
  const panelRgb = hslRgb(h, 80, l1 + 4);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(h, 80, l1)},0.92)`,
  };
};

/**
 * Shared body for the slower "ranged" Live themes — hue oscillates between
 * baseH-amp and baseH+amp at the given hue period, lightness pulses on its
 * own period. Each themed wrapper just supplies the palette parameters.
 */
function rangedLiveAnim(
  ms: number,
  baseH: number, amp: number, sat: number,
  huePeriod: number, pulsePeriod: number,
): Record<string, string> {
  const h = baseH + amp * Math.sin((ms / huePeriod) * Math.PI * 2);
  const lPulse = (Math.sin((ms / pulsePeriod) * Math.PI * 2) + 1) / 2;
  const l1 = 5 + lPulse * 7;
  const l2 = 11 + lPulse * 9;
  const wrap = (x: number) => ((x % 360) + 360) % 360;
  const dark     = hslToHex(wrap(h),       sat,     l1);
  const mid      = hslToHex(wrap(h + 14),  sat - 6, l2);
  const panelRgb = hslRgb(wrap(h), sat - 2, l1 + 4);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(wrap(h), sat, l1)},0.92)`,
  };
}

/** Wave — cool ocean palette, 10 s hue cycle / 7 s pulse. */
const waveAnim = (ms: number) => rangedLiveAnim(ms, 200, 25, 72, 10000, 7000);

/** Glow — warm fire palette, 14 s hue cycle / 9 s pulse. */
const glowAnim = (ms: number) => rangedLiveAnim(ms, 15,  35, 78, 14000, 9000);

/** Drift — purple/violet dusk palette, 18 s hue cycle / 12 s pulse. */
const driftAnim = (ms: number) => rangedLiveAnim(ms, 275, 35, 72, 18000, 12000);

/**
 * Tinsel — red → gold → green three-phase Christmas cycle (9 s total,
 * ~3 s per color). Adds gold as a bridge between the red/green of
 * Jingle, giving a wreath-like festive trio.
 */
const tinselAnim = (ms: number): Record<string, string> => {
  const phases = [
    { h: 355, s: 78, l1: 8,  l2: 16 }, // deep red
    { h: 45,  s: 82, l1: 10, l2: 18 }, // gold
    { h: 142, s: 62, l1: 7,  l2: 14 }, // evergreen
  ];
  const period = 9000;
  const raw = ((ms % period) / period) * phases.length;
  const fi  = Math.floor(raw) % phases.length;
  const ti  = (fi + 1) % phases.length;
  const e   = easeInOut(raw - Math.floor(raw));
  const fr  = phases[fi];
  const to  = phases[ti];
  const h   = lerpAngle(fr.h, to.h, e);
  const s   = fr.s  + (to.s  - fr.s)  * e;
  const l1  = fr.l1 + (to.l1 - fr.l1) * e;
  const l2  = fr.l2 + (to.l2 - fr.l2) * e;
  const dark     = hslToHex(h, s, l1);
  const mid      = hslToHex(h + 10, s - 5, l2);
  const panelRgb = hslRgb(h, s, l1 + 4);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(h, s, l1)},0.92)`,
  };
};

/**
 * Jingle — festive red ↔ green oscillation, 8 s full round-trip. Uses
 * phase blending (not sine) so the bg sits longer on each color than
 * in between, with brief eased transitions.
 */
const jingleAnim = (ms: number): Record<string, string> => {
  const phases = [
    { h: 355, s: 78, l1: 8,  l2: 16 }, // deep festive red
    { h: 142, s: 62, l1: 7,  l2: 14 }, // evergreen
  ];
  const period = 8000;
  const raw = ((ms % period) / period) * phases.length;
  const fi  = Math.floor(raw) % phases.length;
  const ti  = (fi + 1) % phases.length;
  const e   = easeInOut(raw - Math.floor(raw));
  const fr  = phases[fi];
  const to  = phases[ti];
  const h   = lerpAngle(fr.h, to.h, e);
  const s   = fr.s  + (to.s  - fr.s)  * e;
  const l1  = fr.l1 + (to.l1 - fr.l1) * e;
  const l2  = fr.l2 + (to.l2 - fr.l2) * e;
  const dark     = hslToHex(h, s, l1);
  const mid      = hslToHex(h + 8, s - 4, l2);
  const panelRgb = hslRgb(h, s, l1 + 4);
  return {
    '--shell-bg':       `linear-gradient(160deg, ${dark} 0%, ${mid} 55%, ${dark} 100%)`,
    '--shell-surface':  dark,
    '--no-cam-bg':      mid,
    '--modal-card-bg':  mid,
    '--modal-inset-bg': dark,
    '--hint-bar-bg':    mid,
    '--panel-bg':       `rgba(${panelRgb},0.88)`,
    '--card-bg':        `rgba(${panelRgb},0.94)`,
    '--card-bg-soft':   `rgba(${panelRgb},0.82)`,
    '--btn-stop-bg':    `rgba(${hslRgb(h, s, l1)},0.92)`,
  };
};

/** Built-in theme definitions. Tokens listed here are the only ones that differ
 *  from the default SCSS values; everything else falls through to the stylesheet. */
const BUILT_IN_THEMES: RecorderTheme[] = [
  {
    id: 'default',
    name: 'Default',
    builtIn: true,
    tokens: {},
  },
  {
    id: 'claude-colorblind',
    name: 'Accessible',
    builtIn: true,
    tokens: {
      '--shell-bg':             'linear-gradient(160deg, #04071c 0%, #07102a 55%, #04071c 100%)',
      '--shell-surface':        '#04071c',
      '--no-cam-bg':            '#07102a',
      '--modal-card-bg':        '#07102a',
      '--modal-inset-bg':       '#04071c',
      '--ss-mint':              '#2563eb',
      '--ui-accent':            '#2563eb',
      '--ui-accent-soft':       'rgba(37, 99, 235, 0.18)',
      '--accent-focus-ring':    'rgba(37, 99, 235, 0.55)',
      '--accent-glow':          'rgba(37, 99, 235, 0.22)',
      '--accent-glow-hover':    'rgba(37, 99, 235, 0.32)',
      '--accent-hover-bg':      'rgba(37, 99, 235, 0.15)',
      '--track-fill':           '#2563eb',
      '--control-bg-active':    'rgba(37, 99, 235, 0.22)',
      '--ss-pink':              '#ea7600',
      '--ss-green':             '#0096d6',
      '--text-on-accent':       '#ffffff',
      '--text-on-pink':         '#ffffff',
      '--color-ok':             '#2563eb',
      '--color-ok-soft':        'rgba(37, 99, 235, 0.12)',
      '--color-ok-border':      'rgba(37, 99, 235, 0.25)',
      '--color-success':        '#0096d6',
      '--color-success-soft':   'rgba(0, 150, 214, 0.10)',
      '--color-success-border': 'rgba(0, 150, 214, 0.28)',
      '--color-error':          '#d95d00',
      '--color-error-soft':     'rgba(217, 93, 0, 0.12)',
      '--color-error-border':   'rgba(217, 93, 0, 0.28)',
      '--color-warning':        '#c49000',
      '--color-warning-soft':   'rgba(196, 144, 0, 0.14)',
      '--color-warning-border': 'rgba(196, 144, 0, 0.35)',
      '--color-info':           '#2563eb',
      '--color-info-soft':      'rgba(37, 99, 235, 0.12)',
      '--color-info-border':    'rgba(37, 99, 235, 0.35)',
    },
  },
  {
    id: 'aurora',
    name: 'Aurora',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #08041a 0%, #130828 55%, #08041a 100%)',
      '--no-cam-bg':         '#08041a',
      '--modal-card-bg':     '#130828',
      '--modal-inset-bg':    '#08041a',
      '--ss-mint':           '#a855f7',
      '--ui-accent':         '#a855f7',
      '--ui-accent-soft':    'rgba(168,85,247,0.18)',
      '--accent-focus-ring': 'rgba(168,85,247,0.55)',
      '--accent-glow':       'rgba(168,85,247,0.22)',
      '--accent-glow-hover': 'rgba(168,85,247,0.32)',
      '--accent-hover-bg':   'rgba(168,85,247,0.15)',
      '--track-fill':        '#a855f7',
      '--control-bg-active': 'rgba(168,85,247,0.22)',
      '--ss-pink':           '#ec4899',
      '--color-ok':          '#a855f7',
      '--color-ok-soft':     'rgba(168,85,247,0.12)',
      '--color-ok-border':   'rgba(168,85,247,0.25)',
    },
  },
  {
    id: 'ember',
    name: 'Ember',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #0e0703 0%, #1a0b04 55%, #0e0703 100%)',
      '--no-cam-bg':         '#0e0703',
      '--modal-card-bg':     '#1a0b04',
      '--modal-inset-bg':    '#0e0703',
      '--ss-mint':           '#f59e0b',
      '--ui-accent':         '#f59e0b',
      '--ui-accent-soft':    'rgba(245,158,11,0.18)',
      '--accent-focus-ring': 'rgba(245,158,11,0.55)',
      '--accent-glow':       'rgba(245,158,11,0.22)',
      '--accent-glow-hover': 'rgba(245,158,11,0.32)',
      '--accent-hover-bg':   'rgba(245,158,11,0.15)',
      '--track-fill':        '#f59e0b',
      '--control-bg-active': 'rgba(245,158,11,0.22)',
      '--ss-pink':           '#ef4444',
      '--color-ok':          '#f59e0b',
      '--color-ok-soft':     'rgba(245,158,11,0.12)',
      '--color-ok-border':   'rgba(245,158,11,0.25)',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #1b2030 0%, #222840 55%, #1b2030 100%)',
      '--shell-surface':     '#1b2030',
      '--no-cam-bg':         '#222840',
      '--ss-navy':           '#10141e',
      '--ss-mint':           '#3ac5c7',
      '--ss-pink':           '#d63e6a',
      '--ss-green':          '#1fb37a',
      '--ui-accent':         '#3ac5c7',
      '--ui-accent-soft':    'rgba(58,197,199,0.18)',
      '--accent-focus-ring': 'rgba(58,197,199,0.55)',
      '--accent-glow':       'rgba(58,197,199,0.22)',
      '--accent-glow-hover': 'rgba(58,197,199,0.32)',
      '--accent-hover-bg':   'rgba(58,197,199,0.12)',
      '--track-fill':        '#3ac5c7',
      '--control-bg-active': 'rgba(58,197,199,0.20)',
      '--color-ok':          '#3ac5c7',
      '--color-ok-soft':     'rgba(58,197,199,0.12)',
      '--color-ok-border':   'rgba(58,197,199,0.25)',
    },
  },
  {
    id: 'cool',
    name: 'Cool',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #010e1c 0%, #021828 40%, #010c18 100%)',
      '--shell-surface':     '#010e1c',
      '--no-cam-bg':         '#021828',
      '--ss-navy':           '#031420',
      '--ss-mint':           '#00cfff',
      '--ss-pink':           '#e879f9',
      '--ss-green':          '#06d6a0',
      '--ui-accent':         '#00cfff',
      '--ui-accent-soft':    'rgba(0,207,255,0.18)',
      '--accent-focus-ring': 'rgba(0,207,255,0.55)',
      '--accent-glow':       'rgba(0,207,255,0.22)',
      '--accent-glow-hover': 'rgba(0,207,255,0.32)',
      '--accent-hover-bg':   'rgba(0,207,255,0.12)',
      '--track-fill':        '#00cfff',
      '--control-bg-active': 'rgba(0,207,255,0.20)',
      '--text-on-accent':    '#010e1c',
      '--text-on-pink':      '#ffffff',
      '--card-bg-soft':      'rgba(2,22,42,0.78)',
      '--card-bg':           'rgba(2,22,42,0.90)',
      '--panel-bg':          'rgba(2,18,36,0.82)',
      '--modal-card-bg':     '#021828',
      '--modal-inset-bg':    '#010e1c',
      '--hint-bar-bg':       '#021828',
      '--hint-bar-text':     '#cce8f4',
      '--btn-stop-bg':       'rgba(1,14,28,0.88)',
      '--color-ok':          '#00cfff',
      '--color-ok-soft':     'rgba(0,207,255,0.12)',
      '--color-ok-border':   'rgba(0,207,255,0.25)',
    },
  },
  {
    id: 'warm',
    name: 'Warm',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #1a0700 0%, #2e1100 45%, #1a0700 100%)',
      '--shell-surface':     '#1a0700',
      '--no-cam-bg':         '#1a0700',
      '--ss-navy':           '#2a0f00',
      '--ss-mint':           '#ff9a3c',
      '--ss-pink':           '#ff4757',
      '--ss-green':          '#f0c040',
      '--ui-accent':         '#ff9a3c',
      '--ui-accent-soft':    'rgba(255,154,60,0.18)',
      '--accent-focus-ring': 'rgba(255,154,60,0.55)',
      '--accent-glow':       'rgba(255,154,60,0.22)',
      '--accent-glow-hover': 'rgba(255,154,60,0.32)',
      '--accent-hover-bg':   'rgba(255,154,60,0.12)',
      '--track-fill':        '#ff9a3c',
      '--control-bg-active': 'rgba(255,154,60,0.20)',
      '--text-on-accent':    '#1a0700',
      '--text-on-pink':      '#ffffff',
      '--card-bg-soft':      'rgba(38,14,0,0.78)',
      '--card-bg':           'rgba(38,14,0,0.90)',
      '--panel-bg':          'rgba(30,10,0,0.82)',
      '--modal-card-bg':     '#2a1000',
      '--modal-inset-bg':    '#1a0700',
      '--hint-bar-bg':       '#2a1000',
      '--hint-bar-text':     '#ffe0c0',
      '--btn-stop-bg':       'rgba(26,7,0,0.88)',
      '--color-ok':          '#ff9a3c',
      '--color-ok-soft':     'rgba(255,154,60,0.12)',
      '--color-ok-border':   'rgba(255,154,60,0.25)',
    },
  },
  {
    id: 'romantic',
    name: 'Romantic',
    builtIn: true,
    fillLight: {
      dark: [22, 2, 24], light: [255, 120, 195],
      tone: { cool: [210, 170, 220], warm: [255, 180, 140] },
    },
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #160218 0%, #260430 50%, #160218 100%)',
      '--shell-surface':     '#160218',
      '--no-cam-bg':         '#160218',
      '--ss-navy':           '#220330',
      '--ss-mint':           '#ff6eb4',
      '--ss-pink':           '#ff1d6e',
      '--ss-green':          '#c084fc',
      '--ui-accent':         '#ff6eb4',
      '--ui-accent-soft':    'rgba(255,110,180,0.18)',
      '--accent-focus-ring': 'rgba(255,110,180,0.55)',
      '--accent-glow':       'rgba(255,110,180,0.28)',
      '--accent-glow-hover': 'rgba(255,110,180,0.38)',
      '--accent-hover-bg':   'rgba(255,110,180,0.12)',
      '--track-fill':        '#ff6eb4',
      '--control-bg-active': 'rgba(255,110,180,0.20)',
      '--text-on-accent':    '#160218',
      '--text-on-pink':      '#ffffff',
      '--card-bg-soft':      'rgba(36,4,36,0.78)',
      '--card-bg':           'rgba(36,4,36,0.90)',
      '--panel-bg':          'rgba(28,2,28,0.82)',
      '--modal-card-bg':     '#220430',
      '--modal-inset-bg':    '#160218',
      '--hint-bar-bg':       '#220430',
      '--hint-bar-text':     '#ffd6ec',
      '--btn-stop-bg':       'rgba(22,2,24,0.88)',
      '--color-ok':          '#ff6eb4',
      '--color-ok-soft':     'rgba(255,110,180,0.12)',
      '--color-ok-border':   'rgba(255,110,180,0.25)',
    },
  },

  // ── Music & Stage ────────────────────────────────────────────────────────
  {
    id: 'synthwave', name: 'Synthwave', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #080010 0%, #100020 50%, #060010 100%)',
      '--shell-surface':     '#080010', '--no-cam-bg': '#100020', '--ss-navy': '#060010',
      '--ss-mint': '#ff2d78', '--ss-pink': '#00f5d4', '--ss-green': '#a855f7',
      '--ui-accent': '#ff2d78', '--ui-accent-soft': 'rgba(255,45,120,0.18)',
      '--accent-focus-ring': 'rgba(255,45,120,0.55)', '--accent-glow': 'rgba(255,45,120,0.22)',
      '--accent-glow-hover': 'rgba(255,45,120,0.32)', '--accent-hover-bg': 'rgba(255,45,120,0.12)',
      '--track-fill': '#ff2d78', '--control-bg-active': 'rgba(255,45,120,0.20)',
      '--text-on-accent': '#ffffff', '--text-on-pink': '#080010',
      '--card-bg-soft': 'rgba(16,0,32,0.78)', '--card-bg': 'rgba(16,0,32,0.90)',
      '--panel-bg': 'rgba(12,0,24,0.82)', '--modal-card-bg': '#100020', '--modal-inset-bg': '#080010',
      '--hint-bar-bg': '#100020', '--hint-bar-text': '#ffd0e8', '--btn-stop-bg': 'rgba(8,0,16,0.88)',
      '--color-ok': '#ff2d78', '--color-ok-soft': 'rgba(255,45,120,0.12)', '--color-ok-border': 'rgba(255,45,120,0.25)',
    },
  },
  {
    id: 'jazz-club', name: 'Jazz Club', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #1e1608 0%, #342410 55%, #1e1608 100%)',
      '--shell-surface':     '#1e1608', '--no-cam-bg': '#342410', '--ss-navy': '#160e04',
      '--ss-mint': '#d4a843', '--ss-pink': '#c23b22', '--ss-green': '#e8c070',
      '--ui-accent': '#d4a843', '--ui-accent-soft': 'rgba(212,168,67,0.18)',
      '--accent-focus-ring': 'rgba(212,168,67,0.55)', '--accent-glow': 'rgba(212,168,67,0.22)',
      '--accent-glow-hover': 'rgba(212,168,67,0.32)', '--accent-hover-bg': 'rgba(212,168,67,0.12)',
      '--track-fill': '#d4a843', '--control-bg-active': 'rgba(212,168,67,0.20)',
      '--text-on-accent': '#160e04', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(14,10,4,0.90)', '--card-bg': 'rgba(14,10,4,0.96)',
      '--panel-bg': 'rgba(10,6,2,0.92)', '--modal-card-bg': '#1a1008', '--modal-inset-bg': '#120c04',
      '--hint-bar-bg': '#1a1008', '--hint-bar-text': '#f0d8b0', '--btn-stop-bg': 'rgba(10,6,2,0.94)',
      '--color-ok': '#d4a843', '--color-ok-soft': 'rgba(212,168,67,0.12)', '--color-ok-border': 'rgba(212,168,67,0.25)',
    },
  },
  {
    id: 'vinyl-retro', name: 'Vinyl / Retro', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #2b1d14 0%, #3d2a1a 55%, #2b1d14 100%)',
      '--shell-surface':     '#2b1d14', '--no-cam-bg': '#3d2a1a', '--ss-navy': '#1c120a',
      '--ss-mint': '#e06a38', '--ss-pink': '#a83a28', '--ss-green': '#c99a5a',
      '--ui-accent': '#e06a38', '--ui-accent-soft': 'rgba(224,106,56,0.18)',
      '--accent-focus-ring': 'rgba(224,106,56,0.55)', '--accent-glow': 'rgba(224,106,56,0.22)',
      '--accent-glow-hover': 'rgba(224,106,56,0.32)', '--accent-hover-bg': 'rgba(224,106,56,0.12)',
      '--track-fill': '#e06a38', '--control-bg-active': 'rgba(224,106,56,0.20)',
      '--text-on-accent': '#1c120a', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(20,12,6,0.90)', '--card-bg': 'rgba(20,12,6,0.96)',
      '--panel-bg': 'rgba(14,8,4,0.92)', '--modal-card-bg': '#241812', '--modal-inset-bg': '#1a1008',
      '--hint-bar-bg': '#241812', '--hint-bar-text': '#f0c8a8', '--btn-stop-bg': 'rgba(14,8,4,0.94)',
      '--color-ok': '#e06a38', '--color-ok-soft': 'rgba(224,106,56,0.12)', '--color-ok-border': 'rgba(224,106,56,0.25)',
    },
  },
  {
    id: 'rock-stage', name: 'Rock Stage', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #080808 0%, #101010 55%, #080808 100%)',
      '--shell-surface':     '#080808', '--no-cam-bg': '#101010', '--ss-navy': '#040404',
      '--ss-mint': '#c8cdd6', '--ss-pink': '#ff2020', '--ss-green': '#888888',
      '--ui-accent': '#c8cdd6', '--ui-accent-soft': 'rgba(200,205,214,0.18)',
      '--accent-focus-ring': 'rgba(200,205,214,0.55)', '--accent-glow': 'rgba(200,205,214,0.22)',
      '--accent-glow-hover': 'rgba(200,205,214,0.32)', '--accent-hover-bg': 'rgba(200,205,214,0.12)',
      '--track-fill': '#c8cdd6', '--control-bg-active': 'rgba(200,205,214,0.20)',
      '--text-on-accent': '#080808', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(16,16,16,0.78)', '--card-bg': 'rgba(16,16,16,0.90)',
      '--panel-bg': 'rgba(10,10,10,0.82)', '--modal-card-bg': '#101010', '--modal-inset-bg': '#080808',
      '--hint-bar-bg': '#101010', '--hint-bar-text': '#c8c8c8', '--btn-stop-bg': 'rgba(8,8,8,0.92)',
      '--color-ok': '#c8cdd6', '--color-ok-soft': 'rgba(200,205,214,0.12)', '--color-ok-border': 'rgba(200,205,214,0.25)',
    },
  },
  {
    id: 'neon-tokyo', name: 'Neon Tokyo', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #04020e 0%, #080418 55%, #04020e 100%)',
      '--shell-surface':     '#04020e', '--no-cam-bg': '#080418', '--ss-navy': '#030212',
      '--ss-mint': '#00ffe7', '--ss-pink': '#ff00a8', '--ss-green': '#a855f7',
      '--ui-accent': '#00ffe7', '--ui-accent-soft': 'rgba(0,255,231,0.18)',
      '--accent-focus-ring': 'rgba(0,255,231,0.55)', '--accent-glow': 'rgba(0,255,231,0.22)',
      '--accent-glow-hover': 'rgba(0,255,231,0.32)', '--accent-hover-bg': 'rgba(0,255,231,0.12)',
      '--track-fill': '#00ffe7', '--control-bg-active': 'rgba(0,255,231,0.20)',
      '--text-on-accent': '#04020e', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(8,4,24,0.78)', '--card-bg': 'rgba(8,4,24,0.90)',
      '--panel-bg': 'rgba(6,2,18,0.82)', '--modal-card-bg': '#080418', '--modal-inset-bg': '#04020e',
      '--hint-bar-bg': '#080418', '--hint-bar-text': '#c0f8f4', '--btn-stop-bg': 'rgba(4,2,14,0.88)',
      '--color-ok': '#00ffe7', '--color-ok-soft': 'rgba(0,255,231,0.12)', '--color-ok-border': 'rgba(0,255,231,0.25)',
    },
  },
  {
    id: 'disco', name: 'Disco', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #04071c 0%, #080e28 50%, #06041a 100%)',
      '--shell-surface':     '#04071c', '--no-cam-bg': '#080e28', '--ss-navy': '#030514',
      '--ss-mint': '#ffd700', '--ss-pink': '#ff7b00', '--ss-green': '#ff1493',
      '--ui-accent': '#ffd700', '--ui-accent-soft': 'rgba(255,215,0,0.18)',
      '--accent-focus-ring': 'rgba(255,215,0,0.55)', '--accent-glow': 'rgba(255,215,0,0.22)',
      '--accent-glow-hover': 'rgba(255,215,0,0.32)', '--accent-hover-bg': 'rgba(255,215,0,0.12)',
      '--track-fill': '#ffd700', '--control-bg-active': 'rgba(255,215,0,0.20)',
      '--text-on-accent': '#04071c', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(8,14,40,0.78)', '--card-bg': 'rgba(8,14,40,0.90)',
      '--panel-bg': 'rgba(4,8,28,0.82)', '--modal-card-bg': '#080e28', '--modal-inset-bg': '#04071c',
      '--hint-bar-bg': '#080e28', '--hint-bar-text': '#fff0c0', '--btn-stop-bg': 'rgba(4,7,28,0.88)',
      '--color-ok': '#ffd700', '--color-ok-soft': 'rgba(255,215,0,0.12)', '--color-ok-border': 'rgba(255,215,0,0.25)',
    },
  },
  {
    id: 'edm-rave', name: 'EDM / Rave', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #060606 0%, #0c0c10 55%, #060606 100%)',
      '--shell-surface':     '#060606', '--no-cam-bg': '#0c0c10', '--ss-navy': '#040404',
      '--ss-mint': '#bcff00', '--ss-pink': '#9b00ff', '--ss-green': '#ff00cc',
      '--ui-accent': '#bcff00', '--ui-accent-soft': 'rgba(188,255,0,0.18)',
      '--accent-focus-ring': 'rgba(188,255,0,0.55)', '--accent-glow': 'rgba(188,255,0,0.22)',
      '--accent-glow-hover': 'rgba(188,255,0,0.32)', '--accent-hover-bg': 'rgba(188,255,0,0.12)',
      '--track-fill': '#bcff00', '--control-bg-active': 'rgba(188,255,0,0.20)',
      '--text-on-accent': '#060606', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(12,12,16,0.78)', '--card-bg': 'rgba(12,12,16,0.90)',
      '--panel-bg': 'rgba(8,8,12,0.82)', '--modal-card-bg': '#0c0c10', '--modal-inset-bg': '#060606',
      '--hint-bar-bg': '#0c0c10', '--hint-bar-text': '#d8ff80', '--btn-stop-bg': 'rgba(6,6,6,0.92)',
      '--color-ok': '#bcff00', '--color-ok-soft': 'rgba(188,255,0,0.12)', '--color-ok-border': 'rgba(188,255,0,0.25)',
    },
  },
  {
    id: 'studio-pro', name: 'Studio Pro', builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #2a2a2a 0%, #3c3c3c 55%, #2a2a2a 100%)',
      '--shell-surface':     '#2a2a2a', '--no-cam-bg': '#3c3c3c', '--ss-navy': '#1c1c1c',
      '--ss-mint': '#3ddc84', '--ss-pink': '#e03030', '--ss-green': '#00b4d8',
      '--ui-accent': '#3ddc84', '--ui-accent-soft': 'rgba(61,220,132,0.18)',
      '--accent-focus-ring': 'rgba(61,220,132,0.55)', '--accent-glow': 'rgba(61,220,132,0.22)',
      '--accent-glow-hover': 'rgba(61,220,132,0.32)', '--accent-hover-bg': 'rgba(61,220,132,0.12)',
      '--track-fill': '#3ddc84', '--control-bg-active': 'rgba(61,220,132,0.20)',
      '--text-on-accent': '#1c1c1c', '--text-on-pink': '#ffffff',
      '--card-bg-soft': 'rgba(22,22,22,0.88)', '--card-bg': 'rgba(22,22,22,0.94)',
      '--panel-bg': 'rgba(18,18,18,0.90)', '--modal-card-bg': '#222222', '--modal-inset-bg': '#181818',
      '--hint-bar-bg': '#222222', '--hint-bar-text': '#c0f0d8', '--btn-stop-bg': 'rgba(18,18,18,0.94)',
      '--color-ok': '#3ddc84', '--color-ok-soft': 'rgba(61,220,132,0.12)', '--color-ok-border': 'rgba(61,220,132,0.25)',
    },
  },

  // ── Live / animated themes ─────────────────────────────────────────────────
  {
    id: 'aurora-live',
    name: 'Northern Lights',
    builtIn: true,
    animation: auroraLiveAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #060c1e 0%, #0b1628 55%, #060c1e 100%)',
      '--shell-surface':     '#060c1e',
      '--no-cam-bg':         '#0b1628',
      '--ss-navy':           '#03070f',
      '--ss-mint':           '#58c7c9',
      '--ss-pink':           '#a78bfa',
      '--ss-green':          '#2dd4bf',
      '--ui-accent':         '#58c7c9',
      '--ui-accent-soft':    'rgba(88,199,201,0.18)',
      '--accent-focus-ring': 'rgba(88,199,201,0.55)',
      '--accent-glow':       'rgba(88,199,201,0.22)',
      '--accent-glow-hover': 'rgba(88,199,201,0.32)',
      '--accent-hover-bg':   'rgba(88,199,201,0.12)',
      '--track-fill':        '#58c7c9',
      '--control-bg-active': 'rgba(88,199,201,0.20)',
      '--color-ok':          '#58c7c9',
      '--color-ok-soft':     'rgba(88,199,201,0.12)',
      '--color-ok-border':   'rgba(88,199,201,0.25)',
      '--card-bg-soft':      'rgba(6,12,30,0.90)',
      '--card-bg':           'rgba(6,12,30,0.96)',
      '--panel-bg':          'rgba(3,7,15,0.92)',
      '--modal-card-bg':     '#0b1628',
      '--modal-inset-bg':    '#060c1e',
      '--hint-bar-bg':       '#0b1628',
      '--hint-bar-text':     '#58c7c9',
      '--btn-stop-bg':       'rgba(3,7,15,0.94)',
    },
  },
  {
    id: 'sunrise',
    name: 'Sunrise',
    builtIn: true,
    animation: sunriseAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(180deg, #16123a 0%, #3a1a5e 34%, #8a2668 58%, #c23f42 75%, #e0982f 89%)',
      '--shell-surface':     '#16123a',
      '--no-cam-bg':         '#2a1650',
      '--ss-navy':           '#0d0a24',
      '--ss-mint':           '#ff6fa0',
      '--ss-pink':           '#ff4d8d',
      '--ss-green':          '#ff9ec4',
      '--ui-accent':         '#ff6fa0',
      '--ui-accent-soft':    'rgba(255,111,160,0.18)',
      '--accent-focus-ring': 'rgba(255,111,160,0.55)',
      '--accent-glow':       'rgba(255,111,160,0.22)',
      '--accent-glow-hover': 'rgba(255,111,160,0.32)',
      '--accent-hover-bg':   'rgba(255,111,160,0.12)',
      '--track-fill':        '#ff6fa0',
      '--control-bg-active': 'rgba(255,111,160,0.20)',
      '--color-ok':          '#ff6fa0',
      '--color-ok-soft':     'rgba(255,111,160,0.12)',
      '--color-ok-border':   'rgba(255,111,160,0.25)',
      '--card-bg-soft':      'rgba(18,14,40,0.90)',
      '--card-bg':           'rgba(18,14,40,0.96)',
      '--panel-bg':          'rgba(12,9,30,0.92)',
      '--modal-card-bg':     '#201542',
      '--modal-inset-bg':    '#16123a',
      '--hint-bar-bg':       '#201542',
      '--hint-bar-text':     '#ffb0cd',
      '--btn-stop-bg':       'rgba(12,9,30,0.94)',
    },
  },
  {
    id: 'breathe',
    name: 'Breathe',
    builtIn: true,
    animation: breatheAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #04071c 0%, #080e28 55%, #04071c 100%)',
      '--shell-surface':     '#04071c',
      '--no-cam-bg':         '#080e28',
      '--ss-navy':           '#020412',
      '--ss-mint':           '#58c7c9',
      '--ss-pink':           '#e35887',
      '--ss-green':          '#5ad08a',
      '--ui-accent':         '#58c7c9',
      '--ui-accent-soft':    'rgba(88,199,201,0.18)',
      '--accent-focus-ring': 'rgba(88,199,201,0.55)',
      '--accent-glow':       'rgba(88,199,201,0.22)',
      '--accent-glow-hover': 'rgba(88,199,201,0.32)',
      '--accent-hover-bg':   'rgba(88,199,201,0.12)',
      '--track-fill':        '#58c7c9',
      '--control-bg-active': 'rgba(88,199,201,0.20)',
      '--color-ok':          '#58c7c9',
      '--color-ok-soft':     'rgba(88,199,201,0.12)',
      '--color-ok-border':   'rgba(88,199,201,0.25)',
      '--card-bg-soft':      'rgba(4,7,28,0.90)',
      '--card-bg':           'rgba(4,7,28,0.96)',
      '--panel-bg':          'rgba(2,4,18,0.92)',
      '--modal-card-bg':     '#080e28',
      '--modal-inset-bg':    '#04071c',
      '--hint-bar-bg':       '#080e28',
      '--hint-bar-text':     '#58c7c9',
      '--btn-stop-bg':       'rgba(2,4,18,0.94)',
    },
  },
  {
    id: 'pulse',
    name: 'Pulse',
    builtIn: true,
    animation: pulseAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #1a0218 0%, #2d0430 55%, #1a0218 100%)',
      '--shell-surface':     '#1a0218',
      '--no-cam-bg':         '#2d0430',
      '--ss-navy':           '#0d010e',
      '--ss-mint':           '#ec4899',
      '--ss-pink':           '#fbbf24',
      '--ss-green':          '#a855f7',
      '--ui-accent':         '#ec4899',
      '--ui-accent-soft':    'rgba(236,72,153,0.18)',
      '--accent-focus-ring': 'rgba(236,72,153,0.55)',
      '--accent-glow':       'rgba(236,72,153,0.22)',
      '--accent-glow-hover': 'rgba(236,72,153,0.32)',
      '--accent-hover-bg':   'rgba(236,72,153,0.12)',
      '--track-fill':        '#ec4899',
      '--control-bg-active': 'rgba(236,72,153,0.20)',
      '--color-ok':          '#ec4899',
      '--color-ok-soft':     'rgba(236,72,153,0.12)',
      '--color-ok-border':   'rgba(236,72,153,0.25)',
      '--card-bg-soft':      'rgba(26,2,24,0.90)',
      '--card-bg':           'rgba(26,2,24,0.96)',
      '--panel-bg':          'rgba(13,1,14,0.92)',
      '--modal-card-bg':     '#2d0430',
      '--modal-inset-bg':    '#1a0218',
      '--hint-bar-bg':       '#2d0430',
      '--hint-bar-text':     '#ec4899',
      '--btn-stop-bg':       'rgba(13,1,14,0.94)',
    },
  },
  {
    id: 'wave',
    name: 'Wave',
    builtIn: true,
    animation: waveAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #02101a 0%, #04223a 55%, #02101a 100%)',
      '--shell-surface':     '#02101a',
      '--no-cam-bg':         '#04223a',
      '--ss-navy':           '#01080d',
      '--ss-mint':           '#06b6d4',
      '--ss-pink':           '#22d3ee',
      '--ss-green':          '#0891b2',
      '--ui-accent':         '#06b6d4',
      '--ui-accent-soft':    'rgba(6,182,212,0.18)',
      '--accent-focus-ring': 'rgba(6,182,212,0.55)',
      '--accent-glow':       'rgba(6,182,212,0.22)',
      '--accent-glow-hover': 'rgba(6,182,212,0.32)',
      '--accent-hover-bg':   'rgba(6,182,212,0.12)',
      '--track-fill':        '#06b6d4',
      '--control-bg-active': 'rgba(6,182,212,0.20)',
      '--color-ok':          '#06b6d4',
      '--color-ok-soft':     'rgba(6,182,212,0.12)',
      '--color-ok-border':   'rgba(6,182,212,0.25)',
      '--card-bg-soft':      'rgba(2,16,26,0.90)',
      '--card-bg':           'rgba(2,16,26,0.96)',
      '--panel-bg':          'rgba(1,8,13,0.92)',
      '--modal-card-bg':     '#04223a',
      '--modal-inset-bg':    '#02101a',
      '--hint-bar-bg':       '#04223a',
      '--hint-bar-text':     '#06b6d4',
      '--btn-stop-bg':       'rgba(1,8,13,0.94)',
    },
  },
  {
    id: 'glow',
    name: 'Glow',
    builtIn: true,
    animation: glowAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #1a0604 0%, #2e0e08 55%, #1a0604 100%)',
      '--shell-surface':     '#1a0604',
      '--no-cam-bg':         '#2e0e08',
      '--ss-navy':           '#0d0302',
      '--ss-mint':           '#f97316',
      '--ss-pink':           '#fbbf24',
      '--ss-green':          '#ef4444',
      '--ui-accent':         '#f97316',
      '--ui-accent-soft':    'rgba(249,115,22,0.18)',
      '--accent-focus-ring': 'rgba(249,115,22,0.55)',
      '--accent-glow':       'rgba(249,115,22,0.22)',
      '--accent-glow-hover': 'rgba(249,115,22,0.32)',
      '--accent-hover-bg':   'rgba(249,115,22,0.12)',
      '--track-fill':        '#f97316',
      '--control-bg-active': 'rgba(249,115,22,0.20)',
      '--color-ok':          '#f97316',
      '--color-ok-soft':     'rgba(249,115,22,0.12)',
      '--color-ok-border':   'rgba(249,115,22,0.25)',
      '--card-bg-soft':      'rgba(26,6,4,0.90)',
      '--card-bg':           'rgba(26,6,4,0.96)',
      '--panel-bg':          'rgba(13,3,2,0.92)',
      '--modal-card-bg':     '#2e0e08',
      '--modal-inset-bg':    '#1a0604',
      '--hint-bar-bg':       '#2e0e08',
      '--hint-bar-text':     '#f97316',
      '--btn-stop-bg':       'rgba(13,3,2,0.94)',
    },
  },
  {
    id: 'drift',
    name: 'Drift',
    builtIn: true,
    animation: driftAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #100420 0%, #1f0a3a 55%, #100420 100%)',
      '--shell-surface':     '#100420',
      '--no-cam-bg':         '#1f0a3a',
      '--ss-navy':           '#080210',
      '--ss-mint':           '#a855f7',
      '--ss-pink':           '#d946ef',
      '--ss-green':          '#7c3aed',
      '--ui-accent':         '#a855f7',
      '--ui-accent-soft':    'rgba(168,85,247,0.18)',
      '--accent-focus-ring': 'rgba(168,85,247,0.55)',
      '--accent-glow':       'rgba(168,85,247,0.22)',
      '--accent-glow-hover': 'rgba(168,85,247,0.32)',
      '--accent-hover-bg':   'rgba(168,85,247,0.12)',
      '--track-fill':        '#a855f7',
      '--control-bg-active': 'rgba(168,85,247,0.20)',
      '--color-ok':          '#a855f7',
      '--color-ok-soft':     'rgba(168,85,247,0.12)',
      '--color-ok-border':   'rgba(168,85,247,0.25)',
      '--card-bg-soft':      'rgba(16,4,32,0.90)',
      '--card-bg':           'rgba(16,4,32,0.96)',
      '--panel-bg':          'rgba(8,2,16,0.92)',
      '--modal-card-bg':     '#1f0a3a',
      '--modal-inset-bg':    '#100420',
      '--hint-bar-bg':       '#1f0a3a',
      '--hint-bar-text':     '#a855f7',
      '--btn-stop-bg':       'rgba(8,2,16,0.94)',
    },
  },
  {
    id: 'jingle',
    name: 'Jingle',
    builtIn: true,
    animation: jingleAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #14040a 0%, #2a0a12 55%, #14040a 100%)',
      '--shell-surface':     '#14040a',
      '--no-cam-bg':         '#2a0a12',
      '--ss-navy':           '#0a0205',
      '--ss-mint':           '#dc2626',
      '--ss-pink':           '#16a34a',
      '--ss-green':          '#eab308',
      '--ui-accent':         '#dc2626',
      '--ui-accent-soft':    'rgba(220,38,38,0.18)',
      '--accent-focus-ring': 'rgba(220,38,38,0.55)',
      '--accent-glow':       'rgba(220,38,38,0.22)',
      '--accent-glow-hover': 'rgba(220,38,38,0.32)',
      '--accent-hover-bg':   'rgba(220,38,38,0.12)',
      '--track-fill':        '#dc2626',
      '--control-bg-active': 'rgba(220,38,38,0.20)',
      '--color-ok':          '#16a34a',
      '--color-ok-soft':     'rgba(22,163,74,0.12)',
      '--color-ok-border':   'rgba(22,163,74,0.25)',
      '--card-bg-soft':      'rgba(20,4,10,0.90)',
      '--card-bg':           'rgba(20,4,10,0.96)',
      '--panel-bg':          'rgba(10,2,5,0.92)',
      '--modal-card-bg':     '#2a0a12',
      '--modal-inset-bg':    '#14040a',
      '--hint-bar-bg':       '#2a0a12',
      '--hint-bar-text':     '#eab308',
      '--btn-stop-bg':       'rgba(10,2,5,0.94)',
    },
  },
  {
    id: 'tinsel',
    name: 'Tinsel',
    builtIn: true,
    animation: tinselAnim,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #18100a 0%, #2e1f10 55%, #18100a 100%)',
      '--shell-surface':     '#18100a',
      '--no-cam-bg':         '#2e1f10',
      '--ss-navy':           '#0a0604',
      '--ss-mint':           '#eab308',
      '--ss-pink':           '#dc2626',
      '--ss-green':          '#16a34a',
      '--ui-accent':         '#eab308',
      '--ui-accent-soft':    'rgba(234,179,8,0.18)',
      '--accent-focus-ring': 'rgba(234,179,8,0.55)',
      '--accent-glow':       'rgba(234,179,8,0.22)',
      '--accent-glow-hover': 'rgba(234,179,8,0.32)',
      '--accent-hover-bg':   'rgba(234,179,8,0.12)',
      '--track-fill':        '#eab308',
      '--control-bg-active': 'rgba(234,179,8,0.20)',
      '--color-ok':          '#16a34a',
      '--color-ok-soft':     'rgba(22,163,74,0.12)',
      '--color-ok-border':   'rgba(22,163,74,0.25)',
      '--card-bg-soft':      'rgba(24,16,10,0.90)',
      '--card-bg':           'rgba(24,16,10,0.96)',
      '--panel-bg':          'rgba(10,6,4,0.92)',
      '--modal-card-bg':     '#2e1f10',
      '--modal-inset-bg':    '#18100a',
      '--hint-bar-bg':       '#2e1f10',
      '--hint-bar-text':     '#eab308',
      '--btn-stop-bg':       'rgba(10,6,4,0.94)',
    },
  },

  {
    id: 'white',
    name: 'White',
    builtIn: true,
    tokens: {
      '--shell-bg':          'linear-gradient(160deg, #e8ecf4 0%, #eef0f5 55%, #e8ecf4 100%)',
      '--shell-surface':     '#eef0f5',
      '--shell-edge':        'rgba(238,240,245,0.98)',
      '--no-cam-bg':         '#dde2ed',
      '--ss-navy':           '#dde2ed',
      '--ss-mint':           '#0d8d90',
      '--ss-pink':           '#c4325f',
      '--ss-green':          '#1a9e58',
      '--ui-accent':         '#0d8d90',
      '--ui-accent-soft':    'rgba(13,141,144,0.14)',
      '--accent-focus-ring': 'rgba(13,141,144,0.45)',
      '--accent-glow':       'rgba(13,141,144,0.18)',
      '--accent-glow-hover': 'rgba(13,141,144,0.28)',
      '--accent-hover-bg':   'rgba(13,141,144,0.10)',
      '--track-fill':        '#0d8d90',
      '--control-bg-active': 'rgba(13,141,144,0.16)',
      '--ui-fg-strong':      '#0a0e20',
      '--ui-fg':             '#1e2b3c',
      '--ui-fg-muted':       '#4a5568',
      '--ui-fg-subtle':      '#718096',
      '--ui-fg-dim':         '#94a3b8',
      '--text-on-accent':    '#ffffff',
      '--text-on-pink':      '#ffffff',
      '--ui-border':         'rgba(10,14,32,0.09)',
      '--ui-border-strong':  'rgba(10,14,32,0.16)',
      '--ui-divider':        'rgba(10,14,32,0.05)',
      '--panel-bg':          '#ffffff',
      '--panel-border':      'rgba(10,14,32,0.07)',
      '--panel-shadow':      '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05)',
      '--card-bg':           '#ffffff',
      '--card-bg-soft':      '#ffffff',
      '--card-shadow':       '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05)',
      '--control-bg':        'rgba(10,14,32,0.05)',
      '--control-bg-hover':  'rgba(10,14,32,0.09)',
      '--control-bg-muted':  'rgba(10,14,32,0.10)',
      '--input-bg':          'rgba(10,14,32,0.03)',
      '--track-bg':          'rgba(10,14,32,0.08)',
      '--disabled-fg':       'rgba(10,14,32,0.30)',
      '--disabled-bg':       'rgba(10,14,32,0.04)',
      '--modal-backdrop-bg': 'rgba(238,240,245,0.85)',
      '--modal-card-bg':     '#ffffff',
      '--modal-inset-bg':    '#f4f6fa',
      '--hint-bar-bg':       '#f4f6fa',
      '--hint-bar-text':     '#1e2b3c',
      '--hint-bar-border':   'rgba(10,14,32,0.09)',
      '--btn-stop-bg':       '#ffffff',
      '--btn-stop-border':   'rgba(10,14,32,0.16)',
      '--color-ok':          '#0d8d90',
      '--color-ok-soft':     'rgba(13,141,144,0.10)',
      '--color-ok-border':   'rgba(13,141,144,0.22)',
      '--lyric-text-shadow': 'none',
      '--scrubbar-gradient': 'linear-gradient(to top, rgba(0,0,0,0.06) 0%, transparent 100%)',
    },
  },

];

// ── Theme-builder helpers ─────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}

function scaleRgb({ r, g, b }: { r: number; g: number; b: number }, f: number) {
  return { r: r * f, g: g * f, b: b * f };
}

function luminance({ r, g, b }: { r: number; g: number; b: number }): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Lift a hex color to a "lit" version of itself: convert to HSL, raise the
 * lightness to 92% while preserving hue and dampened saturation. Used as
 * the brightness=100 endpoint for themes without explicit fillLight.light,
 * and as the right end of the brightness slider's gradient preview.
 */
export function liftToLight(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6;
    }
  }
  const targetL = 0.78;
  const targetS = Math.min(s, 0.35);
  const hueToRgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let nr: number, ng: number, nb: number;
  if (targetS === 0) {
    nr = ng = nb = targetL;
  } else {
    const q = targetL < 0.5 ? targetL * (1 + targetS) : targetL + targetS - targetL * targetS;
    const p = 2 * targetL - q;
    nr = hueToRgb(p, q, h + 1 / 3);
    ng = hueToRgb(p, q, h);
    nb = hueToRgb(p, q, h - 1 / 3);
  }
  return [Math.round(nr * 255), Math.round(ng * 255), Math.round(nb * 255)];
}

/**
 * Build a complete RecorderTheme token map from three hex seeds. Same logic
 * as ThemeService.buildTheme — extracted to module scope so built-in themes
 * can reuse it for quick declaration.
 */
function buildThemeTokens(bgHex: string, accentHex: string, highlightHex: string): Record<string, string> {
  const bg = hexToRgb(bgHex);
  const ac = hexToRgb(accentHex);
  const hi = hexToRgb(highlightHex);

  const bgDark    = scaleRgb(bg, 0.65);
  const bgDarker  = scaleRgb(bg, 0.40);
  const bgDarkHex   = rgbToHex(bgDark);
  const bgDarkerHex = rgbToHex(bgDarker);

  const sw3 = { r: (ac.r + hi.r) / 2, g: (ac.g + hi.g) / 2, b: (ac.b + hi.b) / 2 };
  const textOnAccent = luminance(ac) > 0.45 ? '#030303' : '#ffffff';
  const textOnHi     = luminance(hi) > 0.45 ? '#030303' : '#ffffff';

  const r = (c: { r: number; g: number; b: number }) => `${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)}`;

  const tokens: Record<string, string> = {
    '--shell-bg':          `linear-gradient(160deg, ${bgDarkHex} 0%, ${bgHex} 55%, ${bgDarkHex} 100%)`,
    '--shell-surface':     bgDarkHex,
    '--no-cam-bg':         bgHex,
    '--ss-navy':           bgDarkerHex,

    '--ss-mint':           accentHex,
    '--ss-pink':           highlightHex,
    '--ss-green':          rgbToHex(sw3),

    '--ui-accent':         accentHex,
    '--ui-accent-soft':    `rgba(${ac.r},${ac.g},${ac.b},0.18)`,
    '--accent-focus-ring': `rgba(${ac.r},${ac.g},${ac.b},0.55)`,
    '--accent-glow':       `rgba(${ac.r},${ac.g},${ac.b},0.22)`,
    '--accent-glow-hover': `rgba(${ac.r},${ac.g},${ac.b},0.32)`,
    '--accent-hover-bg':   `rgba(${ac.r},${ac.g},${ac.b},0.12)`,
    '--track-fill':        accentHex,
    '--control-bg-active': `rgba(${ac.r},${ac.g},${ac.b},0.20)`,

    '--text-on-accent':    textOnAccent,
    '--text-on-pink':      textOnHi,

    '--card-bg-soft': `rgba(${r(bgDark)},0.90)`,
    '--card-bg':      `rgba(${r(bgDark)},0.96)`,
    '--panel-bg':     `rgba(${r(bgDarker)},0.92)`,
    '--modal-card-bg':  bgHex,
    '--modal-inset-bg': bgDarkHex,
    '--hint-bar-bg':    bgHex,
    '--hint-bar-text':  accentHex,
    '--btn-stop-bg':    `rgba(${r(bgDarker)},0.94)`,

    '--color-ok':        accentHex,
    '--color-ok-soft':   `rgba(${ac.r},${ac.g},${ac.b},0.12)`,
    '--color-ok-border': `rgba(${ac.r},${ac.g},${ac.b},0.25)`,

    // Light-background themes need dark text and white surfaces
    ...(luminance(bg) > 0.55 ? (() => {
      const [tr, tg, tb] = [10, 14, 32];
      const rgba = (a: number) => `rgba(${tr},${tg},${tb},${a})`;
      return {
        '--ui-fg-strong':      `rgb(${tr},${tg},${tb})`,
        '--ui-fg':             rgba(0.82),
        '--ui-fg-muted':       rgba(0.54),
        '--ui-fg-subtle':      rgba(0.36),
        '--ui-fg-dim':         rgba(0.26),
        '--ui-border':         rgba(0.09),
        '--ui-border-strong':  rgba(0.16),
        '--ui-divider':        rgba(0.05),
        '--control-bg':        rgba(0.05),
        '--control-bg-hover':  rgba(0.09),
        '--control-bg-muted':  rgba(0.10),
        '--input-bg':          rgba(0.03),
        '--track-bg':          rgba(0.09),
        '--disabled-fg':       rgba(0.28),
        '--disabled-bg':       rgba(0.04),
        '--card-bg-soft':      '#ffffff',
        '--card-bg':           '#ffffff',
        '--panel-bg':          'rgba(255,255,255,0.88)',
        '--modal-card-bg':     '#ffffff',
        '--modal-inset-bg':    bgHex,
        '--hint-bar-bg':       bgHex,
        '--btn-stop-bg':       '#ffffff',
        '--lyric-text-shadow': 'none',
        '--scrubbar-gradient': 'linear-gradient(to top, rgba(0,0,0,0.06) 0%, transparent 100%)',
      };
    })() : {}),
  };

  return tokens;
}

// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_ACTIVE = 'ss_theme_active';
const STORAGE_CUSTOM = 'ss_theme_custom';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private _customThemes: RecorderTheme[] = this._loadCustom();
  private _activeTokenKeys: string[] = [];

  /** Bumped whenever the active theme changes — components can read this
   *  to re-evaluate theme-derived computed signals. */
  readonly themeVersion = signal(0);

  private _rafId: number | null = null;
  private _rafStart: number | null = null;
  private _rafElapsed = 0;
  private _animFn: ((ms: number) => Record<string, string>) | null = null;

  constructor() {
    this._restore();
  }

  get themes(): RecorderTheme[] {
    return [...BUILT_IN_THEMES, ...this._customThemes];
  }

  get activeThemeId(): string {
    return localStorage.getItem(STORAGE_ACTIVE) ?? 'default';
  }

  get activeTheme(): RecorderTheme {
    return this.themes.find(t => t.id === this.activeThemeId) ?? BUILT_IN_THEMES[0];
  }

  /** Returns three preview accent colors for a theme swatch. */
  previewColors(theme: RecorderTheme): { mint: string; pink: string; green: string } {
    return {
      mint:  theme.tokens['--ss-mint']  ?? '#58c7c9',
      pink:  theme.tokens['--ss-pink']  ?? '#e35887',
      green: theme.tokens['--ss-green'] ?? '#5ad08a',
    };
  }

  /**
   * Returns cool/warm tone endpoints for a theme. Uses explicit
   * `fillLight.tone` if defined, otherwise derives from the theme accent
   * so every theme produces theme-tinted fill light. Remove this method
   * (and the call in `storeFillLightStyle`) to revert to the universal
   * cool→white→warm range for every non-explicit theme.
   */
  toneFor(theme: RecorderTheme): {
    cool: [number, number, number];
    warm: [number, number, number];
  } {
    const explicit = theme.fillLight?.tone;
    if (explicit) return explicit;

    const hex = theme.tokens['--ui-accent'] ?? theme.tokens['--ss-mint'] ?? '#58c7c9';
    const ar = parseInt(hex.slice(1, 3), 16);
    const ag = parseInt(hex.slice(3, 5), 16);
    const ab = parseInt(hex.slice(5, 7), 16);

    const mix = (
      base: [number, number, number],
      accent: [number, number, number],
      f: number,
    ): [number, number, number] => [
      Math.round(base[0] * (1 - f) + accent[0] * f),
      Math.round(base[1] * (1 - f) + accent[1] * f),
      Math.round(base[2] * (1 - f) + accent[2] * f),
    ];

    // Tint the standard cool/warm endpoints by 30% of the accent so the
    // result still reads as cool/warm light but carries the theme's color.
    const cool = mix([160, 200, 255], [ar, ag, ab], 0.30);
    const warm = mix([255, 180, 80],  [ar, ag, ab], 0.30);
    return { cool, warm };
  }

  applyTheme(theme: RecorderTheme): void {
    this._stopRaf();
    const root = document.documentElement;
    this._activeTokenKeys.forEach(t => root.style.removeProperty(t));
    Object.entries(theme.tokens).forEach(([k, v]) => root.style.setProperty(k, v));
    this._activeTokenKeys = Object.keys(theme.tokens);
    localStorage.setItem(STORAGE_ACTIVE, theme.id);
    if (theme.animation) {
      this._animFn = theme.animation;
      this._rafElapsed = 0;
      this._startRaf();
    } else {
      this._animFn = null;
    }
    this.themeVersion.update(v => v + 1);
  }

  private _startRaf(): void {
    const fn = this._animFn;
    if (!fn) return;
    const elapsed0 = this._rafElapsed;
    const step = (ts: number) => {
      if (this._rafStart === null) this._rafStart = ts - elapsed0;
      this._rafElapsed = ts - this._rafStart;
      const tokens = fn(this._rafElapsed);
      const root = document.documentElement;
      Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
      this._rafId = requestAnimationFrame(step);
    };
    this._rafId = requestAnimationFrame(step);
    this._rafStart = null;
  }

  private _stopRaf(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._rafStart = null;
  }

  /** Apply tokens live (e.g. builder preview) without persisting the theme id. */
  previewTokens(tokens: Record<string, string>): void {
    const root = document.documentElement;
    this._activeTokenKeys.forEach(t => root.style.removeProperty(t));
    Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
    this._activeTokenKeys = Object.keys(tokens);
  }

  /**
   * Build a complete RecorderTheme from just three hex colors.
   * bgHex      = the mid-point background color (gradient is derived from it)
   * accentHex  = primary accent / UI controls
   * highlightHex = secondary accent (record button, hover states)
   */
  /**
   * Build a custom theme. If `brightHex` is provided, the theme also gets an
   * explicit `fillLight: { dark, light }` — `dark` = the bg color, `light` =
   * `brightHex`. The Brightness slider then animates from bg → brightHex
   * (Romantic-style dramatic shift). Omit to fall back to the auto-derived
   * `liftToLight` endpoint.
   */
  buildTheme(name: string, bgHex: string, accentHex: string, highlightHex: string, brightHex?: string): RecorderTheme {
    const theme: RecorderTheme = {
      id: `custom-${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}`,
      name,
      builtIn: false,
      tokens: buildThemeTokens(bgHex, accentHex, highlightHex),
    };
    if (brightHex) {
      const dark = hexToRgb(bgHex);
      const light = hexToRgb(brightHex);
      theme.fillLight = {
        dark:  [dark.r,  dark.g,  dark.b],
        light: [light.r, light.g, light.b],
      };
    }
    return theme;
  }

  /** Persist a pre-built RecorderTheme to custom theme storage. */
  saveTheme(theme: RecorderTheme): void {
    this._customThemes = [...this._customThemes, theme];
    this._saveCustom();
  }

  /** Replace an existing custom theme by id. No-op if id is not found. */
  updateTheme(theme: RecorderTheme): void {
    this._customThemes = this._customThemes.map(t => t.id === theme.id ? theme : t);
    this._saveCustom();
  }

  /** Import a theme from parsed JSON (from a .singsnap-theme.json file). */
  importTheme(json: { name?: string; tokens?: Record<string, string> }): RecorderTheme {
    const theme: RecorderTheme = {
      id: 'custom-' + Date.now(),
      name: json.name ?? 'Custom Theme',
      builtIn: false,
      tokens: json.tokens ?? {},
    };
    this._customThemes = [...this._customThemes, theme];
    this._saveCustom();
    return theme;
  }

  deleteCustomTheme(id: string): void {
    this._customThemes = this._customThemes.filter(t => t.id !== id);
    this._saveCustom();
    if (this.activeThemeId === id) {
      this.applyTheme(BUILT_IN_THEMES[0]);
    }
  }

  private _restore(): void {
    const id = this.activeThemeId;
    const theme = this.themes.find(t => t.id === id);
    if (theme) this.applyTheme(theme);
  }

  private _loadCustom(): RecorderTheme[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_CUSTOM) ?? '[]');
    } catch {
      return [];
    }
  }

  private _saveCustom(): void {
    localStorage.setItem(STORAGE_CUSTOM, JSON.stringify(this._customThemes));
  }
}
